import React, { useState, useEffect } from "react";
import { useGlobal } from "reactn";
import { Redirect, useLocation, useHistory, useRouteMatch } from "react-router-dom";

import ReactPlayer from "react-player";
import LazyLoad from "react-lazyload";

import { useLoginState, useUserPremiumState } from "lib/hooks/userHooks";
import { useJSONFetcher } from "@outplayed/json-fetcher";

import { Item, SummonerSpell, ChampionSkill } from "@outplayed/tooltips";
import ComboModal from "components/Champions/Combos/ComboModal";
import Error404 from "components/Error404";
import ComboSoon from "components/Champions/Combos/ComboSoon";

const skillMap = {
  Q: 0,
  W: 1,
  E: 2,
  R: 3,
};

const colorMap = {
  Hard: "#ff9b00",
  Medium: "#08a6ff",
  Easy: "#7ed321",
  Smurf: "#ff4e50",
};

const ComboVid = (props) => {
  const { data } = props;
  const location = useLocation();
  const [rate, setRate] = useState(1.0);
  const [cNorm, setCNorm] = useState("#08a6ff");
  const [cSlow, setCSlow] = useState("#31334a");

  useEffect(() => {
    setRate(1.0);
    setCNorm("#08a6ff");
    setCSlow("#31334a");
  }, [location]);

  const norm = (e) => {
    setRate(1.0);
    setCNorm("#08a6ff");
    setCSlow("#31334a");
  };

  const slow = (e) => {
    setRate(0.5);
    setCNorm("#31334a");
    setCSlow("#08a6ff");
  };

  const thumbLink = `https://i.vimeocdn.com/video/${data.vimeo_pic_id}_773x435.jpg`;

  return (
    <div className="combo-vid-container">
      <div className="combo-vid">
        <ReactPlayer
          url={data.link}
          playing={true}
          loop={true}
          controls={true}
          light={thumbLink}
          playbackRate={rate}
          width={773}
          height={435}
        />
      </div>
      <div className="title-control-container">
        <div className="combo-title">{data.name}</div>
        <div className="control">
          <div className="control-title">Playback Speed</div>
          <div className="speed-control">
            <button className="speed-btn" onClick={norm} style={{ backgroundColor: `${cNorm}` }}>
              <p className="btn-txt">1x</p>
            </button>
            <button className="speed-btn" onClick={slow} style={{ backgroundColor: `${cSlow}` }}>
              <p className="btn-txt">0.5x</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComboList = (props) => {
  const { combos, championId } = props;

  if (combos && championId) {
    return (
      <div className="combos">
        {combos.map((step, index) => {
          if (step.Type === "Skill") {
            return (
              <ChampionSkill
                key={index}
                className={"step"}
                championId={championId}
                skillSlot={skillMap[step.Value]}
                disableTooltip
              />
            );
          }
          if (step.Type === "Summoner") {
            return <SummonerSpell key={index} className="step" spellId={step.Value} disableTooltip />;
          }
          if (step.Type === "Item") {
            return <Item key={index} className="step" itemId={step.Value} disableTooltip />;
          }
          if (step.Type === "AutoAttack") {
            return <img className="step" key={index} src="https://static.bigbrain.gg/assets/lol/combos/imgs/AA.svg" alt="aa" />;
          }
          if (step.Type === "Special") {
            return (
              <img
                className="step"
                key={index}
                src={`https://static.bigbrain.gg/assets/lol/combos/imgs/specials/${step.Value}.png`}
                alt="special"
              />
            );
          }
        })}
      </div>
    );
  } else {
    return null;
  }
};

const Recomm = (props) => {
  const { videoId, name, diff, id, champ, vimeoPicId } = props;
  const history = useHistory();
  const location = useLocation();

  const goCombo = (e) => {
    if (history && id && champ) {
      history.push(`/lol/champions/${champ}/combo/${id}`);
    }
  };

  const comboSet = location.pathname.split("/")[5];
  const thumbLink = `https://i.vimeocdn.com/video/${vimeoPicId}_192x108.jpg`;

  let color = "#31334a";

  if (Number(comboSet) === Number(id)) {
    color = "#dba54b";
  }

  return (
    <div className="recomm-container" onClick={goCombo}>
      <div className="thumb-container">
        <LazyLoad height={108}>
          <img className="thumb" style={{ borderColor: `${color}` }} src={thumbLink} alt="thumbnail" />
        </LazyLoad>
        {Number(comboSet) === Number(id) ? (
          <div className="thumb-gold">
            <img className="play" src="https://static.bigbrain.gg/assets/lol/combos/imgs/play.svg" alt="play" />
          </div>
        ) : (
          <div className="thumb-key">
            <p className="id">{id}</p>
          </div>
        )}
      </div>
      <div className="combo-name">{name}</div>
      <div className="diff" style={{ color: `${colorMap[diff]}` }}>
        {diff.toUpperCase()}
      </div>
    </div>
  );
};

const Combo = (props) => {
  const { data: combos, loading: isLoading } = useJSONFetcher(
    `https://static.bigbrain.gg/assets/lol/combos/${process.env.COMBOS_FILENAME}.json`,
  );
  const { isPremium } = useUserPremiumState();
  const [loggingIn] = useGlobal("loggingIn");
  const isLoggedIn = useLoginState();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const { params } = match;
  let { champion, videoId } = params || {};

  if (loggingIn || isLoading) {
    return <div className="spinthatshit-loader" />;
  }

  let combosForChamp = combos[champion];
  if (champion === "wukong") {
    combosForChamp = combos["monkeyking"];
  }

  if (!combosForChamp) {
    return <ComboSoon />;
  }

  if (isNaN(videoId)) {
    return <Redirect to={`/lol/champions/${champion}/combo/1`} />;
  }

  // TODO: refactor this, no need to put into a list and then put
  const allCombos = combosForChamp.combos.map((combo, index) => {
    return [combo.link.split("video/")[1], combo.name, combo.difficulty, combo.id, combo.vimeo_pic_id];
  });
  const data = combosForChamp.combos[Number(videoId) - 1];

  if (!data) {
    return <Error404 />;
  }

  return (
    <div className="combo-page-container">
      <div className="inner-combo-container">
        <div className="combo-main">
          {(!isLoggedIn || !isPremium) && videoId != "1" ? (
            <ComboModal />
          ) : (
            <div>
              <ComboVid data={data} />
              <div className="divider" />
              <div className="desc-container">
                <div className="left-desc">
                  <div className="diff-container">
                    <div className="title-rating">
                      <div className="title">DIFFICULTY</div>
                      <div className="rating" style={{ color: `${colorMap[data.difficulty]}` }}>
                        {data.difficulty.toUpperCase()}
                      </div>
                    </div>
                    <div className="diff-bar">
                      <img
                        src={`https://static.bigbrain.gg/assets/lol/combos/imgs/bar-${data.difficulty}.svg`}
                        alt={data.difficulty}
                      />
                    </div>
                  </div>
                  <div className="skill-combos-container">
                    <div className="title">COMBO</div>
                    <ComboList combos={data.combo} championId={combosForChamp.key} />
                  </div>
                  <div className="use-case-container">
                    <div className="title">USE CASE - {data["use-case-title"]}</div>
                    <div className="desc">{data["use-case-description"]}</div>
                  </div>
                </div>
                <div className="right-desc">
                  <div className="tip-container">
                    <div className="title">EXECUTION</div>
                    <div className="tip">{data.execution}</div>
                  </div>
                  <div className="interaction-container">
                    <div className="title">HOW DOES IT WORK?</div>
                    <ul className="interactions">
                      {data["how-does-it-work"].map((interaction, index) => {
                        return (
                          <li className="item" key={index}>
                            {interaction}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="combo-divider" />
                  <div className="helper-text">
                    We abridged certain advanced concepts so our Combo tips would be both accessible for new users and informative
                    for seasoned players. As a result, some technical language is generalized into more familiar terms for
                    everyone. For example, “cast time” is a general term we used to include both traditional cast time and lockout
                    time.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="combo-recom">
          {allCombos &&
            allCombos.map((combo, index) => {
              return (
                <Recomm
                  videoId={combo[0]}
                  name={combo[1]}
                  diff={combo[2]}
                  id={combo[3]}
                  vimeoPicId={combo[4]}
                  champ={champion}
                  key={index}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default Combo;
